import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import File from '../library/File';
import ReportTile from './ReportTile';
import ReportFields from '../../services/ReportFields';
import UpdateDueReport from './UpdateDueReport';
import { getFileAndDownload } from '../../actions/fileDownloadAction';
import { changeReportTrackingDisplay } from '../../actions/reportTrackingAction';
import { formatDate, truncateMiddle } from '../../utils/helper';
import styles from '../../styles/report-tracking/DueReportsDetails.module.scss';
import { UncontrolledPopover, PopoverBody } from 'reactstrap';
import {
  ToolTipIcon,
  ArrowCircleFillLeftIcon,
  CloseButtonNormalIcon,
  BoxAndArrowIcon,
} from '../../services/SvgLibrary';
import parse from 'html-react-parser';
import { localLawSort } from '../../components/report-tracking/ReportsTable';

const DueReportsDetails = props => {
  const [reportDetail, setReportDetail] = useState({});
  const [dueDate, setDueDate] = useState(
    props.selectedDay ? props.selectedDay.toLocaleDateString() : ''
  );
  const params = useParams()

  useEffect(() => {
    if (!calendarView || (props.trackerId && !reportDetail.id)) {
      const dueDateDict = getDueDateDict(props.selectedDayDueReports[0]);
      const dateKeys = Object.keys(dueDateDict);
      let lastDate = '';

      if (!dueDate) {
        lastDate = dateKeys[dateKeys.length - 1];
      }
      if (!calendarView) {
        props.changeReportTrackingDisplay('detail');
        props.setHeaderColor(props.selectedDayDueReports[0].status);
      }
      setReportDetail(props.selectedDayDueReports[0]);
      setDueDate(dueDate ? dueDate : lastDate);
    }
    // }, [props, reportDetail.id, dueDate]);
  }, [reportDetail.id, dueDate]);

  const onTileClick = report => {
    props.changeReportTrackingDisplay('detail');
    setReportDetail(report);
    props.onChangeCurrentHead('');
  };

  const { highlightTerms } = props

  const getDueDateDict = attachments => {
    let dueDateDict = {};
    attachments.attachment.forEach(report => {
      let dateString = new Date(report.due_date).toLocaleDateString();
      // For each attachment check if the date key exists, if not create it
      // But only add the attachment to the dict if the attachment has a file
      if (dueDateDict[dateString]) {
        if(report.file) {
          dueDateDict[dateString].push(report);
        }
      } else {
        if (report.file) {
          dueDateDict[dateString] = [report];
        } else {
          dueDateDict[dateString] = [];
        }
      }
    });
    return dueDateDict;
  };

  // Function to convert string with links to <a> tags
  const convertLinksToAnchors = text => {
    // Regular expression to match URLs (HTTP, HTTPS, and FTP)
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.split(urlRegex).map((part, index) => {
      if (urlRegex.test(part)) {
        return (
          <a key={index} href={part} target='_blank' rel='noopener noreferrer'>
            {part}
          </a>
        );
      }
      return part; // Return the text parts without links
    });
  };

  // Generate the compliance requirement details
  const displayReportDetail = reportFields => {
    let details = reportFields.map((field, idx) => {
      // Display a list of committees
      if (field.field === 'committees') {
        const arr = reportDetail['committees'];
        let committees = '';
        for (let i = 0; i < arr.length; i++) {
          committees += arr[i].committee_name;
          committees += ', ';
        }
        return (
          <div key={idx}>
            <p>{field.title}</p> 
            <div>{parse(highlightTerms(committees.substring(0, committees.length - 2)))}</div>
          </div>
        );
      }
      // Display a list of assigned contacts
      else if (field.field.includes('_contact')) {
        const arr = reportDetail[field.field];
        let contactLength = arr.length - 1;
        let links = arr.map((contact, idx) => {
          return (
            <span key={idx}>
              <a
                href={`${window.location.origin}/staff/${
                  contact.user_profile ? contact.user_profile : contact.id
                }`}
                onClick={e => {
                  e.preventDefault();
                  window.open(
                    `${window.location.origin}/staff/${
                      contact.user_profile ? contact.user_profile : contact.id
                    }`
                  );
                }}
                style={{
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.2rem',
                }}>
                {parse(highlightTerms(contact.full_name))} <BoxAndArrowIcon width='12' height='12' />
              </a>
              {contactLength === idx ? '' : ', '}
            </span>
          );
        });
        return (
          <div key={idx}>
            <p>{field.title}</p>
            <div>{links}</div>
          </div>
        );
      } else if (field.field == 'submitted_by') {
        let profile = reportDetail[field.field];
        if (userGroups.includes('Compliance Division')) {
          return (
            <div key={idx}>
              <p>{field.title}</p>
              <div>
                <a
                  href={`${window.location.origin}/staff/${profile.id}`}
                  onClick={e => {
                    e.preventDefault();
                    window.open(
                      `${window.location.origin}/staff/${profile.id}`
                    );
                  }}
                  style={{
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.2rem',
                  }}>
                  {profile.full_name} <BoxAndArrowIcon width='12' height='12' />
                </a>
              </div>
            </div>
          );
        }
      } else if (field.field === 'ls_requests') {
        let lsrsLength = reportDetail['ls_requests'].length - 1;
        const associated_lsrs = reportDetail['ls_requests'].map((ls, idx) => {
          return (
            <span key={ls.ls_number}>
              <a
                href={`${window.location.origin}/lsRequestList/${ls.ls_number}`}
                onClick={e => {
                  e.preventDefault();
                  window.open(
                    `${window.location.origin}/lsRequestList/${ls.ls_number}`
                  );
                }}>
                {ls.ls_number}
              </a>
              {lsrsLength === idx ? '' : ', '}
            </span>
          );
        });
        return (
          <React.Fragment key={idx}>
            <div key={idx}>
              <p>Associated {field.title}</p>
              <div>{associated_lsrs}</div>
            </div>
          </React.Fragment>
        );
      } else if (field.field === 'local_law') {
        let allLocalLaws = reportDetail['legislation'].filter(leg=>{
          if( leg.type === 'local_law' ) {
            return leg
          }
        });
        allLocalLaws = localLawSort(allLocalLaws)
        let linkLength = allLocalLaws.length - 1;

        let allLocalLawLink = allLocalLaws.map((ll, idx2) => {
          return (
            <span key={`${ll.full_law_number}l`}>
              <a href={ll['legistar_url']} target='_blank' rel='noopener noreferrer'>
                {ll.full_law_number}
              </a>
              {linkLength === idx2 ? '' : ', '}
            </span>
          );
        });

        return (
          <React.Fragment key={idx}>
            <div>
              <p>{field.title}</p>
              <div>{allLocalLawLink}</div>
            </div>
          </React.Fragment>
        );
      } else if (field.field === 'legacy_local_law') {
        let legacy_local_laws = reportDetail['legislation'].filter(leg=>{
          if( leg.type === 'legacy_local_law' ) {
            return leg
          }
        })
        legacy_local_laws = localLawSort(legacy_local_laws)
        let linkLength = legacy_local_laws.length - 1;
        let allLocalLawLink = legacy_local_laws.map(
          (ll, idx2) => {
            return (
              <span key={`${ll.full_law_number}l`}>
                {ll.full_law_number}
                {linkLength === idx2 ? '' : ', '}
              </span>
            );
          }
        );

        return (
          <React.Fragment key={idx}>
            <div>
              <p>{field.title}</p>
              <div>{allLocalLawLink}</div>
            </div>
          </React.Fragment>
        );
      } else if (field.field === 'frequency_unit') {
        return (
          <div key={idx}>
            <p className={styles['input-text-title']}>{field.title}</p>
            <ToolTipIcon
              classNameProp={styles['user-guide-field-icon']}
              idProp='unit'
            />
            <UncontrolledPopover
              innerClassName={styles['popover-container']}
              className={styles['popover-outer-container']}
              fade={false}
              trigger='hover'
              placement='right'
              target='unit'>
              <PopoverBody>
                <div>
                  Reports will be requested by the system every Period unit.
                  <br></br>
                  <br></br>
                  The <b>Unit</b> specifies the time-unit used under the
                  <em>Period</em> field.
                  <em>
                    For example, a "Unit” of <b>Months</b>, and a “Period” of
                    <b>3</b> indicates that a report is due every 3 months.
                  </em>
                  The system accepts “Once” or “As needed” as unit options as
                  well.
                </div>
              </PopoverBody>
            </UncontrolledPopover>
            <div className='capitalize'>
              {reportDetail[field.field].replace(/_/g, ' ')}
            </div>
          </div>
        );
      } else if (field.field === 'frequency_quantity') {
        reportDetail[field.field] = reportDetail[field.field]
          ? reportDetail[field.field]
          : 'None';
        return (
          <div key={idx}>
            <p className={styles['input-text-title']}>{field.title}</p>
            <ToolTipIcon
              classNameProp={styles['user-guide-field-icon']}
              idProp='period'
            />
            <UncontrolledPopover
              innerClassName={styles['popover-container']}
              className={styles['popover-outer-container']}
              fade={false}
              trigger='hover'
              placement='right'
              target='period'>
              <PopoverBody>
                <div>
                  Reports will be requested by the system every Period unit.
                  <br></br>
                  <br></br>
                  The <b>Unit</b> specifies the time-unit used under the
                  <em>Period</em> field.
                  <em>
                    For example, a "Unit” of <b>Months</b>, and a “Period” of
                    <b>3</b> indicates that a report is due every 3 months.
                  </em>
                </div>
              </PopoverBody>
            </UncontrolledPopover>
            <div>{reportDetail[field.field]}</div>
          </div>
        );
      } else if (field.field === 'tracker_type') {
        return (
          <div key={idx}>
            <p>{field.title}</p>
            <div className='capitalize'>{parse(highlightTerms(reportDetail[field.field]))}</div>
          </div>
        );
      } else if (field.field === 'submitting_agency') {
        const arr = reportDetail['submitting_agency'];
        let agencies = '';
        for (let i = 0; i < arr.length; i++) {
          agencies += arr[i].name + ', ';
        }

        return (
          <div key={idx}>
            <p>{field.title}</p>
            <div>{parse(highlightTerms(agencies.substring(0, agencies.length - 2)))}</div>
          </div>
        );
      }
      // Display file
      else if (field.type === 'file') {
        let files, dueDateDict;
        if (field.field === 'attachment') {
          files = reportDetail.attachment;
          dueDateDict = getDueDateDict(reportDetail);
        } else {
          files = reportDetail.milestone_attachment;
          dueDateDict = [];
        }

        let getOrderedDates = Object.keys(dueDateDict).sort((a, b) => {
          a = new Date(a);
          b = new Date(b);
          return a > b ? -1 : a < b ? 1 : 0;
        });

        return (
          <div key={idx}>
            <p>{field.title}</p>
            {getOrderedDates.length > 0 && (
              <>
                <div>
                  <select
                    value={dueDate}
                    className={styles['file-dropdown']}
                    onChange={e => {
                      setDueDate(e.target.value);
                    }}>
                    <option value=''>Select Due Date</option>
                    {getOrderedDates.map((date, index) => {
                      let empty =
                        dueDateDict[date].length > 0 ? '' : ' (empty)';
                      let displayDate = new Date(date).toLocaleDateString();
                      return (
                        <option value={date} key={index}>
                          {displayDate + empty}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <br />
                {dueDate && (
                  <div className={styles['report-field-files']}>
                    {dueDateDict[dueDate] &&
                      dueDateDict[dueDate].map((file, idx) => {
                        return (
                          <React.Fragment key={idx}>
                            <File
                              date={file.timestamp}
                              file={file}
                              fileName={
                                file.file_name
                                  ? truncateMiddle(file.file_name, 25)
                                  : null
                              }
                              id={`old-files-${idx}`}
                              getFileAndDownload={props.getFileAndDownload}
                              downloadStatus={props.downloadStatus}
                            />
                            {file.submitted_by && <div>from {file.submitted_by.full_name}</div>}
                          </React.Fragment>
                        );
                      })}
                  </div>
                )}
              </>
            )}
            {field.field === 'milestone_attachment' &&
              files.map((file, idx) => {
                if(file.doc_id || calendarView) {
                  return (
                    <React.Fragment key={idx}>
                      <File
                        date={file.timestamp}
                        file={file}
                        fileName={
                          file.file_name ? truncateMiddle(file.file_name, 25) : null
                        }
                        id={`milestone-file-${idx}`}
                        getFileAndDownload={props.getFileAndDownload}
                        downloadStatus={props.downloadStatus}
                      />
                      {file.submitted_by && <div>from {file.submitted_by.full_name}</div>}
                    </React.Fragment>
                  );
                }
              })
            }
            {reportDetail[field.field] ? '' : 'None'}
          </div>
        );
      } else if (typeof reportDetail[field.field] === 'boolean') {
        return (
          <div key={idx}>
            <p>{field.title}</p>
            <div>{reportDetail[field.field] ? 'True' : 'False'}</div>
          </div>
        );
      } else if (field.type === 'date') {
        return (
          <div key={idx}>
            <p>{field.title}</p>
            <div>
              {reportDetail[field.field]
                ? formatDate(reportDetail[field.field])
                : 'None'}
            </div>
          </div>
        );
      } else if (field.field === 'confirmation') {
        if (reportDetail[field.field].length) {
          let confirm = reportDetail[field.field][0];
          let confirm_date = new Date(
            confirm['confirm_date']
          ).toLocaleDateString();
          let create_date = new Date(
            confirm['created_at']
          ).toLocaleDateString();
          
          let rule_publication_date = confirm['rule_publication_date']
            ? new Date(confirm['rule_publication_date']).toLocaleDateString()
            : '';
          let rule_final_adoption_date = confirm['rule_final_adoption_date']
            ? new Date(confirm['rule_final_adoption_date']).toLocaleDateString()
            : '';
          let rule_effective_date = confirm['rule_effective_date']
            ? new Date(confirm['rule_effective_date']).toLocaleDateString()
            : '';
          const fileDisplayName = url => {
            if (!url) return '';
            try {
              return decodeURIComponent(url.split('?')[0].split('/').pop());
            } catch {
              return url;
            }
          };
          return (
            <div>
              <p>{parse(field.title)}</p>
              <ul className={styles['report-details']}>
                <li>Name: {confirm['confirm_name']}</li>
                <li>Job Title: {confirm['confirm_title']}</li>
                <li>Agency: {confirm['confirm_agency']['name']}</li>
                <li>Email: {confirm['confirm_email']}</li>
                {confirm['confirm_notes'] && (
                  <li>
                    Notes: {convertLinksToAnchors(confirm['confirm_notes'])}
                  </li>
                )}
                <li>Date Completed: {confirm_date}</li>
                <li>Submitted At: {create_date}</li>
                {rule_publication_date && (
                  <li>Date of Publication: {rule_publication_date}</li>
                )}
                {rule_final_adoption_date && (
                  <li>Date of Final Rule(s) Adoption: {rule_final_adoption_date}</li>
                )}
                {rule_effective_date && (
                  <li>Date Rule(s) Take Effect: {rule_effective_date}</li>
                )}
                {confirm['rule_final_pdf'] && (
                  <li>
                    Final Rule(s) PDF:{' '}
                    <a
                      href={confirm['rule_final_pdf']}
                      target='_blank'
                      rel='noopener noreferrer'>
                      {fileDisplayName(confirm['rule_final_pdf'])}
                    </a>
                  </li>
                )}
                {confirm['website_url'] && (
                  <li>
                    Website:{' '}
                    <a
                      href={confirm['website_url']}
                      target='_blank'
                      rel='noopener noreferrer'>
                      {confirm['website_url']}
                    </a>
                  </li>
                )}
                {confirm['pamphlet_file'] && (
                  <li>
                    Pamphlet:{' '}
                    <a
                      href={confirm['pamphlet_file']}
                      target='_blank'
                      rel='noopener noreferrer'>
                      {fileDisplayName(confirm['pamphlet_file'])}
                    </a>
                  </li>
                )}
                {confirm['pilot_study_file'] && (
                  <li>
                    Pilot/Feasibility Study:{' '}
                    <a
                      href={confirm['pilot_study_file']}
                      target='_blank'
                      rel='noopener noreferrer'>
                      {fileDisplayName(confirm['pilot_study_file'])}
                    </a>
                  </li>
                )}
                {confirm['outreach_campaign_notes'] && (
                  <li>
                    Outreach Campaign:{' '}
                    {convertLinksToAnchors(confirm['outreach_campaign_notes'])}
                  </li>
                )}
                {confirm['dissemination_notes'] && (
                  <li>
                    Dissemination of Information:{' '}
                    {convertLinksToAnchors(confirm['dissemination_notes'])}
                  </li>
                )}
                {confirm['none_of_above_notes'] && (
                  <li>
                    Other Details:{' '}
                    {convertLinksToAnchors(confirm['none_of_above_notes'])}
                  </li>
                )}
              </ul>
            </div>
          );
        }
      } else {
        reportDetail[field.field] = reportDetail[field.field]
          ? reportDetail[field.field]
          : 'None';
        return (
          <div key={idx}>
            <p>{parse(field.title)}</p>
            <div
              style={
                field.type === 'textarea' ? { 'whiteSpace': 'pre-wrap' } : {}
              }>
              {parse(highlightTerms(reportDetail[field.field]))}
            </div>
          </div>
        );
      }
    });

    return <div className={styles['report-field-column']}>{details}</div>;
  };

  const onBackToReport = () => {
    props.changeReportTrackingDisplay('detail');
  };

  const {
    changeReportTrackingDisplay,
    currentDisplay,
    calendarView,
    init,
    setInit,
    onBackToService,
    onChangeCurrentHead,
    onCloseWindow,
    selectedDay,
    selectedDayDueReports,
    setHeaderColor,
    tileClickHandler,
    titleContainerStyle,
    userProfile,
  } = props;
  const isCMStaff =
    userProfile.role.includes('Council Member') ||
    userProfile.under_council_member;
  const reportsList =
    selectedDayDueReports &&
    selectedDayDueReports.map((report, idx) => (
      <div key={idx}>
        <ReportTile
          day={selectedDay}
          status={report.op_status}
          report={report}
          onTileClick={onTileClick}
          onChangeCurrentHead={onChangeCurrentHead}
          onTileClickHandler={tileClickHandler}
          setHeaderColor={setHeaderColor}
        />
        {idx !== selectedDayDueReports.length - 1 && (
          <hr style={{ width: '90%' }} />
        )}
      </div>
    ));
  const userGroups = props.userProfile.groups.map(group => group.name);
  let displayedFields = [];
  if (currentDisplay === 'detail' && reportDetail.id) {
    let type = reportDetail.tracker_type;
    displayedFields = ReportFields.filter(tracker => {
      return (
        (tracker.tracker === 'Both' ||
          tracker.tracker.toLowerCase() === type) &&
        !(tracker.field == 'errors' && isCMStaff)
      );
    });
  }

  let halfOfFields = displayedFields.length / 2;
  let firstHalfOfFields = displayedFields.slice(0, halfOfFields);
  let secondHalfOfFields = displayedFields.slice(halfOfFields);

  // Generate the content in the popup window
  // By default it will be a list of reports with title and committees
  // Once the end users click the title, it will change to the details of that reports
  return (
    <>
      {currentDisplay === 'list' && (
        <div style={{ height: '50vh', overflow: 'auto', padding: '1rem' }}>
          {reportsList}
        </div>
      )}
      {currentDisplay === 'detail' && (
        <>
          <div className={styles['modal-header']} style={titleContainerStyle}>
            {calendarView && !(params && params.id && init) && (
              <button
                className='btn back-icon'
                onClick={() => {
                  if (!calendarView) {
                    onBackToService();
                  } else {
                    onChangeCurrentHead('list');
                    changeReportTrackingDisplay('list');
                  }
                }}>
                <ArrowCircleFillLeftIcon /> Back
              </button>
            )}
            <span className='popup-detail-title'>
              Compliance Tracker Details
            </span>
            <CloseButtonNormalIcon
              classNameProp='popup-close-icon'
              onClickFunction={() => {
                setInit(false);
                onCloseWindow();
              }}
              alt='close-icon'
            />
          </div>
          <div className={styles['report-fields']}>
            {displayReportDetail(firstHalfOfFields)}
            {displayReportDetail(secondHalfOfFields)}
          </div>
          {userProfile.permissions.reportTracking.change_report && (
            <div className={styles['modal-footer']}>
              <button
                type='button'
                className={`submit-button ${styles['footer-button']}`}
                onClick={() => {
                  changeReportTrackingDisplay('update');
                  onChangeCurrentHead('');
                }}>
                Update Compliance Tracker
              </button>
              <span>To upload a report document, update this report.</span>
            </div>
          )}
        </>
      )}
      {currentDisplay === 'update' && (
        <UpdateDueReport
          params={params}
          currentReport={reportDetail}
          onCloseWindow={onCloseWindow}
          onBackToReport={onBackToReport}
          selectedDay={selectedDay}
        />
      )}
    </>
  );
};

const mapStateToProps = state => {
  return {
    dueReports: state.reportTrackingReducer.dueReports,
    selectedDayDueReports: state.reportTrackingReducer.selectedDayDueReports,
    downloadStatus: state.fileDownloadReducer.downloadStatus,
    currentDisplay: state.reportTrackingReducer.currentDisplay,
    userProfile: state.userReducer.userProfile,
  };
};

const mapDispatchToProps = {
  getFileAndDownload,
  changeReportTrackingDisplay,
};

export default connect(mapStateToProps, mapDispatchToProps)(DueReportsDetails);
